import { Channel, CashoutChannel } from "@/websocket/model/messages/channel";
import { MessageType } from "@/websocket/model/enums/message-type";
import { Subject } from "@/websocket/model/messages/subject";
import { User } from "@/websocket/model/messages/user";
import { UserType } from "@/websocket/model/enums/user-type";
import { Body } from "@/websocket/model/messages/body";
import { ChannelType } from "@/websocket/model/enums/channel-type";
import { isV2Protocol } from "@/websocket/composables/use-openbet-ws-client";

interface IResponse {
    type: MessageType;
    msg: string;
}

export class Response implements IResponse {
    public type: MessageType;
    constructor(public msg: string) {
        this.type = Response.getMessageType(msg);
    }

    static getMessageType(msg: string) {
        return msg.charAt(0) as unknown as MessageType;
    }

    static parse(msg: string) {
        switch (Response.getMessageType(msg)) {
            case MessageType.Pong:
                return Response.createInstance(PongResponse, msg);
            case MessageType.PubMsg:
                if (msg.startsWith(ChannelType.Cashout, 1)) {
                    return Response.createInstance(CashoutResponse, msg);
                }
            // eslint-disable-next-line no-fallthrough
            default:
                return Response.createInstance(PubMsgResponse, msg);
        }
    }

    static createInstance<T extends Response>(c: new (msg: string) => T, msg: string): T {
        return new c(msg);
    }
}

export class PongResponse extends Response {
    public id: number;
    constructor(msg: string) {
        super(msg);
        this.id = Number(msg.replace("g", ""));
    }
}

export class PubMsgResponse extends Response {
    private readonly _offset: number;
    public channel: Channel;
    public messageId: string;
    public user: User;
    public subject: Subject;
    public body: Body;

    constructor(msg: string) {
        super(msg);
        const isV2 = isV2Protocol();
        this.channel = Channel.fromString(msg.slice(1, isV2 ? 33 : 17));
        this.messageId = msg.slice(isV2 ? 33 : 17, isV2 ? 43 : 27);
        this.user = User.fromString(msg.slice(isV2 ? 43 : 27, isV2 ? 54 : 38));
        this._offset = this.user.type === UserType.User ? 10 : 0;
        const parseTheRest = (offset: number = this._offset) => {
            this.subject = Subject.fromString(msg.slice((isV2 ? 44 : 28) + offset, (isV2 ? 60 : 44) + offset));
            this.body = JSON.parse(msg.slice((isV2 ? 72 : 56) + offset));
        };
        parseTheRest();
    }
    size() {
        const isV2 = isV2Protocol();
        return {
            bytes: this.msg.slice((isV2 ? 60 : 44) + this._offset, (isV2 ? 66 : 50) + this._offset),
            unicode: this.msg.slice((isV2 ? 66 : 50) + this._offset, (isV2 ? 72 : 56) + this._offset),
        };
    }
}

export class CashoutResponse extends Response {
    private readonly _offset: number;
    public channel: CashoutChannel;
    public messageId: string;
    public user: User;
    public subject: Subject;
    public body: Body;

    constructor(msg: string) {
        super(msg);
        this.channel = CashoutChannel.fromString(msg.slice(1, 33));
        this.messageId = msg.slice(33, 43);
        this.user = User.fromString(msg.slice(43, 54));
        this._offset = this.user.type === UserType.User ? 10 : 0;
        const parseTheRest = (offset: number = this._offset) => {
            this.subject = Subject.fromString(msg.slice(44 + offset, 60 + offset));
            this.body = JSON.parse(msg.slice(72 + offset));
        };
        parseTheRest();
    }
    size() {
        return {
            bytes: this.msg.slice(60 + this._offset, 66 + this._offset),
            unicode: this.msg.slice(66 + this._offset, 72 + this._offset),
        };
    }
}
